export interface Familiar {
  id: string
  name: string
  tier: 'sovereign' | 'adept' | 'guild'
  status: 'draft' | 'active' | 'suspended'
  sigil_url: string | null
  logo_url: string | null
  realm_id: string | null
}

export interface Covenant {
  id: string
  familiar_id: string
  role: string
  tier: string
  tools: Record<string, unknown>
  permissions: Record<string, unknown>
  boundaries: string[]
  escalation: Record<string, unknown>
  memory_scope: Record<string, unknown>
  bond_permissions: Record<string, unknown>
  version: number
}

export interface Message {
  id: string
  role: 'user' | 'assistant'
  content: string
  created_at: string
}

export interface Memory {
  id: string
  type: string
  content: string
  created_at: string
}

export interface Bond {
  id: string
  initiator_familiar_id: string
  target_familiar_id: string
  status: 'pending' | 'active' | 'revoked'
  permissions: Record<string, unknown>
}

export interface Channel {
  id: string
  type: 'telegram' | 'discord' | 'sms'
  active: boolean
}

export interface Circle {
  id: string
  slug: string
  name: string
  warden_keeper_id: string
}

export interface CircleMember {
  keeper_id: string
  role: 'warden' | 'elder' | 'member'
  joined_at: string
}

export interface Realm {
  id: string
  circle_id: string
  slug: string
  name: string
  description: string | null
}

export interface CreateFamiliarPayload {
  name: string
  tier: string
  covenant: { role: string; tools?: Record<string, unknown>; boundaries?: string[] }
  template?: string
  /** Guild tier only: the Realm this familiar serves. Required for tier=guild. */
  realm_id?: string
}

export interface Keeper {
  id: string
  email: string
  display_name: string
}

export interface GateStatus {
  enabled: boolean
  gate_origin: string | null
  /** Present ONLY when code-handoff (CONCLAVE_GATE_SSO) is on and configured.
   *  Its presence is the SPA's signal to do the code-handoff redirect
   *  (client_id + callback) instead of the legacy plain-`next` shared-cookie
   *  bounce. Absent (or older backend) → stay on the shared-cookie path. */
  client_id?: string | null
}

/** Carries the HTTP status so callers can distinguish 401 (not signed in) from other errors. */
export class ApiError extends Error {
  constructor(message: string, readonly status: number) {
    super(message)
    this.name = 'ApiError'
  }
}

export class ConclaveAPI {
  constructor(private baseUrl: string) {}

  private headers(): Record<string, string> {
    return { 'Content-Type': 'application/json' }
  }

  private async req<T>(method: string, path: string, body?: unknown): Promise<T> {
    const res = await fetch(`${this.baseUrl}${path}`, {
      method,
      credentials: 'include', // carry the Warden session cookie; the webview never holds a token
      headers: this.headers(),
      body: body !== undefined ? JSON.stringify(body) : undefined,
    })
    if (!res.ok) {
      const err = await res.json().catch(() => null)
      // auth errors are {detail}; 404/500 are {error:{message}}
      const message = err?.detail ?? err?.error?.message ?? `${method} ${path} failed: ${res.status}`
      throw new ApiError(message, res.status)
    }
    return res.json()
  }

  // ── Auth ──────────────────────────────────────────────────────────────────
  //
  // There is no signup/login here: the browser session is the SHARED gate
  // cookie (one login, founder ruling 2026-08-04). The app only probes who the
  // session resolves to (/auth/me — which JIT-provisions the keeper on first
  // arrival) and where the gate lives (/auth/gate/status).

  logout(): Promise<{ ok: boolean }> {
    return this.req('POST', '/auth/logout', {})
  }

  me(): Promise<Keeper> {
    return this.req('GET', '/auth/me')
  }

  /** Where is the gate? gate_origin relays the backend's single
   *  GATE_PUBLIC_ORIGIN config — the SPA never hardcodes the gate host.
   *  client_id, when present, means code-handoff is on. */
  gateStatus(): Promise<GateStatus> {
    return this.req('GET', '/auth/gate/status')
  }

  /** Redeem the gate's one-time code (server-side, via the Conclave backend).
   *  The backend sets Conclave's own session cookie on this response — the SPA
   *  never sees a token — and returns who the session resolves to. */
  gateExchange(code: string, redirectUri: string): Promise<Keeper> {
    return this.req('POST', '/auth/gate/exchange', { code, redirect_uri: redirectUri })
  }

  // ── Familiars ─────────────────────────────────────────────────────────────

  listFamiliars(tier?: string): Promise<Familiar[]> {
    return this.req('GET', `/familiars${tier ? `?tier=${tier}` : ''}`)
  }

  getFamiliar(id: string): Promise<Familiar> {
    return this.req('GET', `/familiars/${id}`)
  }

  createFamiliar(payload: CreateFamiliarPayload): Promise<Familiar> {
    return this.req('POST', '/familiars', payload)
  }

  activateFamiliar(id: string): Promise<Familiar> {
    return this.req('POST', `/familiars/${id}/activate`, {})
  }

  getCovenant(id: string): Promise<Covenant> {
    return this.req('GET', `/familiars/${id}/covenant`)
  }

  updateCovenant(id: string, patch: Partial<Covenant>): Promise<Covenant> {
    return this.req('PUT', `/familiars/${id}/covenant`, patch)
  }

  // ── Chat ──────────────────────────────────────────────────────────────────

  async chatStream(familiarId: string, message: string): Promise<ReadableStream<Uint8Array>> {
    const res = await fetch(`${this.baseUrl}/familiars/${familiarId}/chat`, {
      method: 'POST',
      credentials: 'include',
      headers: this.headers(),
      body: JSON.stringify({ message }),
    })
    if (!res.ok) throw new Error(`Chat failed: ${res.status}`)
    if (!res.body) throw new Error('No response body')
    return res.body
  }

  getHistory(familiarId: string, limit = 50): Promise<{ messages: Message[] }> {
    return this.req('GET', `/familiars/${familiarId}/history?limit=${limit}`)
  }

  // ── Memory ────────────────────────────────────────────────────────────────

  getMemory(familiarId: string): Promise<{ memories: Memory[] }> {
    return this.req('GET', `/familiars/${familiarId}/memory`)
  }

  addMemory(familiarId: string, content: string, type = 'explicit'): Promise<void> {
    return this.req('POST', `/familiars/${familiarId}/memory`, { content, type })
  }

  // ── Bonds ─────────────────────────────────────────────────────────────────

  listBonds(familiarId: string): Promise<Bond[]> {
    return this.req('GET', `/bonds?familiar_id=${familiarId}`)
  }

  // ── Channels ─────────────────────────────────────────────────────────────

  listChannels(familiarId: string): Promise<Channel[]> {
    return this.req('GET', `/familiars/${familiarId}/channels`)
  }

  activateChannel(familiarId: string, type: string, config: Record<string, unknown>): Promise<Channel> {
    return this.req('POST', `/familiars/${familiarId}/channels`, { type, config })
  }

  deactivateChannel(familiarId: string, type: string): Promise<void> {
    return this.req('DELETE', `/familiars/${familiarId}/channels/${type}`)
  }

  // ── Circles ───────────────────────────────────────────────────────────────

  listCircles(): Promise<Circle[]> {
    return this.req('GET', '/circles')
  }

  getCircle(id: string): Promise<Circle> {
    return this.req('GET', `/circles/${id}`)
  }

  createCircle(name: string, slug: string): Promise<Circle> {
    return this.req('POST', '/circles', { name, slug })
  }

  listMembers(circleId: string): Promise<CircleMember[]> {
    return this.req('GET', `/circles/${circleId}/members`)
  }

  addMember(circleId: string, keeperId: string, role: string): Promise<CircleMember> {
    return this.req('POST', `/circles/${circleId}/members`, { keeper_id: keeperId, role })
  }

  removeMember(circleId: string, keeperId: string): Promise<void> {
    return this.req('DELETE', `/circles/${circleId}/members/${keeperId}`)
  }

  // ── Realms ────────────────────────────────────────────────────────────────

  listRealms(circleId: string): Promise<Realm[]> {
    return this.req('GET', `/circles/${circleId}/realms`)
  }

  getRealm(realmId: string): Promise<Realm> {
    return this.req('GET', `/realms/${realmId}`)
  }

  createRealm(circleId: string, name: string, slug: string, description?: string): Promise<Realm> {
    return this.req('POST', `/circles/${circleId}/realms`, { name, slug, description })
  }

  listRealmFamiliars(realmId: string): Promise<Familiar[]> {
    return this.req('GET', `/realms/${realmId}/familiars`)
  }

  /** Realm-scoped creation is Guild-tier only (the server refuses other tiers). */
  createRealmFamiliar(realmId: string, name: string, role: string): Promise<Familiar> {
    return this.req('POST', `/realms/${realmId}/familiars`, { name, tier: 'guild', role })
  }
}
